import React from 'react';
import { Tabs as AntTabs } from 'antd';
import type { TabsProps as AntTabsProps, TabPaneProps as AntTabPaneProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface TabsProps extends AntTabsProps {
  loading?: boolean;
  className?: string;
}

export interface TabPaneProps extends AntTabPaneProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  // Base tabs container
  tabs: {
    fontFamily: theme.typography.fontFamily.primary,

    // Tab navigation container
    '& .ant-tabs-nav': {
      marginBottom: theme.spacing[4],

      '&::before': {
        borderBottom: 'none',
      },
    },

    // Tab content area
    '& .ant-tabs-content': {
      color: theme.color.semantic.text.primary,
    },

    // Extra content area
    '& .ant-tabs-extra-content': {
      display: 'flex',
      alignItems: 'center',
    },

    // Navigation operations (scroll buttons)
    '& .ant-tabs-nav-operations': {
      display: 'flex',
      alignItems: 'center',
    },

    '& .ant-tabs-nav-more': {
      padding: `${theme.spacing[3]} ${theme.spacing[5]}`,
      color: theme.color.semantic.text.secondary,
      borderColor: theme.color.semantic.border.primary,

      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
  },

  // LINE VARIANT
  lineTabs: {
    '& .ant-tabs-tab': {
      padding: `${theme.spacing[3]} ${theme.spacing[5]}`,
      margin: 0,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      backgroundColor: theme.color.semantic.background.neutral.secondary,
      color: theme.color.semantic.text.secondary,
      transition: 'all 0.3s ease',

      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
      },

      '&:focus': {
        outline: 'none',
      },

      '&:focus-visible': {
        boxShadow: `0 0 0 2px ${theme.color.semantic.brand.default.moderate}`,
      },
    },

    '& .ant-tabs-tab-active': {
      '& .ant-tabs-tab-btn': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
    },

    '& .ant-tabs-tab-disabled': {
      color: `${theme.color.semantic.text.soft} !important`,
      cursor: 'not-allowed',
      borderColor: theme.color.semantic.border.soft,

      '& .ant-tabs-tab-btn': {
        color: `${theme.color.semantic.text.soft} !important`,
      },

      '&:hover': {
        color: `${theme.color.semantic.text.soft} !important`,
      },
    },

    '& .ant-tabs-ink-bar': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      height: theme.border.width.sm,
    },

    // Icon styles for line variant
    '& .ant-tabs-tab .anticon': {
      marginRight: theme.spacing[2],
      color: 'inherit',
    },
  },

  // CARD VARIANT
  cardTabs: {
    '& .ant-tabs-nav': {
      marginBottom: 0,

      '&::before': {
        borderBottom: 'none',
      },
    },

    '& .ant-tabs-tab': {
      padding: `${theme.spacing[3]} ${theme.spacing[5]}`,
      marginRight: theme.spacing[1],
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: theme.color.semantic.text.secondary,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      borderBottom: 'none',
      borderRadius: `${theme.radius.sm} ${theme.radius.sm} 0 0`,
      transition: 'all 0.3s ease',

      '&:hover': {
        color: theme.color.semantic.brand.default.primary,
        borderColor: theme.color.semantic.brand.default.moderate,
      },

      '&:focus': {
        outline: 'none',
      },

      '&:focus-visible': {
        boxShadow: `0 0 0 2px ${theme.color.semantic.brand.default.moderate}`,
      },
    },

    '& .ant-tabs-tab-active': {
      backgroundColor: `${theme.color.semantic.background.neutral.secondary} !important`,
      borderColor: `${theme.color.semantic.brand.default.primary} !important`,

      '& .ant-tabs-tab-btn': {
        color: `${theme.color.semantic.brand.default.primary} !important`,
      },
    },

    '& .ant-tabs-tab-disabled': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      color: `${theme.color.semantic.text.soft} !important`,
      borderColor: theme.color.semantic.border.soft,
      cursor: 'not-allowed',

      '& .ant-tabs-tab-btn': {
        color: `${theme.color.semantic.text.soft} !important`,
      },

      '&:hover': {
        color: `${theme.color.semantic.text.soft} !important`,
        borderColor: theme.color.semantic.border.soft,
      },
    },

    '& .ant-tabs-ink-bar': {
      display: 'none',
    },

    '& .ant-tabs-content-holder': {
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
      borderTop: 'none',
      padding: theme.spacing[5],
      backgroundColor: theme.color.semantic.background.neutral.secondary,
    },

    // Icon styles for card variant
    '& .ant-tabs-tab .anticon': {
      marginRight: theme.spacing[2],
      color: 'inherit',
    },
  },

  // LOADING STATE
  loadingTabs: {
    pointerEvents: 'none',

    '& .ant-tabs-tab': {
      cursor: 'default',
    },

    '& .ant-tabs-tab-btn': {
      color: `${theme.color.semantic.brand.default.primary} !important`,
    },

    '& .ant-tabs-tab .anticon': {
      color: `${theme.color.semantic.brand.default.primary} !important`,
    },

    '& .ant-tabs-ink-bar': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
  },

  // Position variants
  leftTabs: {
    '& .ant-tabs-nav': {
      marginRight: theme.spacing[4],
      marginBottom: 0,

      '&::before': {
        borderRight: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        borderBottom: 'none',
      },
    },

    '& .ant-tabs-tab': {
      marginRight: 0,
      marginBottom: theme.spacing[2],
    },

    '& .ant-tabs-ink-bar': {
      width: theme.border.width.sm,
      height: 'auto !important',
      right: 0,
      left: 'auto',
    },
  },

  rightTabs: {
    '& .ant-tabs-nav': {
      marginLeft: theme.spacing[4],
      marginBottom: 0,

      '&::before': {
        borderLeft: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        borderBottom: 'none',
      },
    },

    '& .ant-tabs-tab': {
      marginRight: 0,
      marginBottom: theme.spacing[2],
    },

    '& .ant-tabs-ink-bar': {
      width: theme.border.width.sm,
      height: 'auto !important',
      left: 0,
    },
  },

  bottomTabs: {
    '& .ant-tabs-nav': {
      marginTop: theme.spacing[4],
      marginBottom: 0,

      '&::before': {
        borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
        borderBottom: 'none',
      },
    },

    '& .ant-tabs-ink-bar': {
      top: 0,
      bottom: 'auto',
    },
  },

  // Centered tabs
  centeredTabs: {
    '& .ant-tabs-nav-list': {
      justifyContent: 'center',
      width: '100%',
    },
  },
}));

export const Tabs: React.FC<TabsProps> & {
  TabPane: typeof AntTabs.TabPane;
} = ({
  type = 'line',
  loading = false,
  tabPosition = 'top',
  centered = false,
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const getTabsClassName = () => {
    const classNames = [classes.tabs];

    // Type classes
    if (type === 'card') {
      classNames.push(classes.cardTabs);
    } else {
      // Default to 'line'
      classNames.push(classes.lineTabs);
    }

    // Position variants
    if (tabPosition === 'left') {
      classNames.push(classes.leftTabs);
    } else if (tabPosition === 'right') {
      classNames.push(classes.rightTabs);
    } else if (tabPosition === 'bottom') {
      classNames.push(classes.bottomTabs);
    }

    // Centered
    if (centered) {
      classNames.push(classes.centeredTabs);
    }

    // Loading state
    if (loading) {
      classNames.push(classes.loadingTabs);
    }

    if (className) {
      classNames.push(className);
    }

    return classNames.join(' ');
  };

  return (
    <AntTabs
      type={type}
      tabPosition={tabPosition}
      centered={centered}
      {...rest}
      className={getTabsClassName()}
    />
  );
};

Tabs.TabPane = AntTabs.TabPane;

export const TabPane = AntTabs.TabPane;

export default Tabs;
